'use client';

import React, { useState } from 'react';
import Link from 'next/link';

// ─── WARD STEWARD ONBOARDING WALKTHROUGH ──────────────────────────────────────

const STEPS = [
  {
    id: 'welcome',
    step: 1,
    title: 'Welcome, Ward Steward',
    arabicTitle: 'مَرْحَبًا بِكَ',
    icon: '◎',
    color: '#2d8a87',
    summary: 'Your role as a Ward Steward is to review, verify, and publish caretaker submissions — resources, disparities, and community context — for your city and ward.',
    content: [
      { label: 'Your Amanah', text: 'Ward Stewards hold the trust of the MRG collection. Every submission you approve becomes part of the living resource guide for your community.' },
      { label: 'What You Steward', text: 'Resources (masajid, food pantries, clinics, legal aid), Disparities (food deserts, healthcare gaps, housing crises), and Context (cultural, historical, community narrative).' },
      { label: 'The Standard', text: 'Verify accuracy. Confirm the resource exists and is accessible. Ensure the disparity data is grounded. Approve what serves the community fisabilillah.' },
    ],
    action: { label: 'Begin Walkthrough →', next: true },
  },
  {
    id: 'submission-review',
    step: 2,
    title: 'Reviewing Submissions',
    arabicTitle: 'مُرَاجَعَة الطَّلَبَات',
    icon: '⬡',
    color: '#2d7a47',
    summary: 'Caretakers submit resources, disparities, and context through the MRG embed form. Your queue shows all pending submissions for your city/ward.',
    content: [
      { label: 'Submission Types', text: 'RESOURCE — a specific organization, program, or service. DISPARITY — a documented gap or inequity. CONTEXT — cultural, historical, or community narrative.' },
      { label: 'Review Checklist', text: '1. Does the resource/disparity exist and serve the community? 2. Is the contact information accurate? 3. Is the Name of Allah attribution appropriate? 4. Does the content align with the Haal Project framework?' },
      { label: 'Pending Queue', text: 'All submissions arrive as PENDING. You can expand each submission to read the full content, see the submitting caretaker, and review tags before taking action.' },
    ],
    action: { label: 'Next: Approval Controls →', next: true },
    demo: {
      type: 'submission-queue',
      items: [
        { type: 'resource', city: 'Chicago', ward: 'Englewood', title: 'Masjid Al-Faatir Food Pantry', status: 'pending', submittedBy: 'caretaker_01' },
        { type: 'disparity', city: 'Chicago', ward: 'Austin', title: 'Healthcare Desert — No Primary Care Within 2 Miles', status: 'pending', submittedBy: 'caretaker_02' },
        { type: 'context', city: 'Atlanta', ward: 'West End', title: 'Growing Somali Community — Cultural Context', status: 'pending', submittedBy: 'caretaker_03' },
      ],
    },
  },
  {
    id: 'approval-controls',
    step: 3,
    title: 'Approval & Publish Controls',
    arabicTitle: 'ضَوَابِط الْمُوَافَقَة',
    icon: '✦',
    color: '#c9a84c',
    summary: 'Each submission has three possible actions: Approve (verified, ready for publishing), Reject (does not meet standards), or Publish (approved and live in the collection).',
    content: [
      { label: 'APPROVE', text: 'Marks the submission as verified. It enters a staging state — approved but not yet live. Use this when you have verified accuracy but want to batch-publish.' },
      { label: 'PUBLISH', text: 'Makes the submission live in the MRG collection immediately. Use this for high-priority resources or urgent disparities that the community needs now.' },
      { label: 'REJECT', text: 'Removes the submission from the queue with a reason. The caretaker can revise and resubmit. Always reject with a clear reason — this is an Amanah.' },
    ],
    action: { label: 'Next: Reading Metrics →', next: true },
    demo: {
      type: 'approval-controls',
      submission: { type: 'resource', title: 'Masjid Al-Faatir Food Pantry', content: 'Weekly food pantry every Saturday 10am-2pm. Open to all community members regardless of faith. Located at 7351 S Stony Island Ave.' },
    },
  },
  {
    id: 'metrics',
    step: 4,
    title: 'Metrics Interpretation',
    arabicTitle: 'قِرَاءَة الْمَقَايِيس',
    icon: '◈',
    color: '#8a8ab8',
    summary: 'The Ward Steward dashboard shows collection metrics by city and ward — resources documented, disparities mapped, caretakers active, and pending submissions.',
    content: [
      { label: 'Collection Metrics', text: 'Resources: total verified resources in the collection. Disparities: documented gaps mapped to Names of Allah. Context: community narratives submitted and approved.' },
      { label: 'Caretaker Activity', text: 'Active caretakers are those who have submitted in the last 30 days. Growing caretaker counts indicate community engagement with the Haal Project framework.' },
      { label: 'Pending Load', text: 'High pending counts indicate active caretaker communities. Aim to review within 48 hours — caretakers are waiting to see their contributions live.' },
    ],
    action: { label: 'Next: Impact Dashboard →', next: true },
    demo: {
      type: 'metrics-grid',
      cities: [
        { city: 'Chicago', resources: 12, disparities: 8, context: 5, pending: 3, caretakers: 24 },
        { city: 'Atlanta', resources: 7, disparities: 4, context: 6, pending: 2, caretakers: 11 },
        { city: 'Detroit', resources: 15, disparities: 6, context: 4, pending: 1, caretakers: 18 },
      ],
    },
  },
  {
    id: 'impact',
    step: 5,
    title: 'Community Impact Dashboard',
    arabicTitle: 'لَوْحَة الأَثَر الْمُجْتَمَعِيّ',
    icon: '⌘',
    color: '#2d7a47',
    summary: 'The impact dashboard tracks how the MRG collection is being used — embed views, resource clicks, caretaker submissions, and quest completions linked to MRG resources.',
    content: [
      { label: 'Embed Reach', text: 'Every MRG embed on an external site (masjid website, community org, activism hub) generates view data. This shows how far the collection is reaching.' },
      { label: 'Resource Utilization', text: 'Which resources are being accessed most? Which wards have the highest disparity-to-resource ratios? This guides where to prioritize new submissions.' },
      { label: 'Quest Connections', text: 'Asbab Quest completions linked to MRG resources show the bridge from resource documentation to community action. This is the Islah loop closing.' },
    ],
    action: { label: 'Go to Ward Steward Dashboard →', href: '/ward-steward' },
    demo: {
      type: 'impact-metrics',
      stats: [
        { label: 'Total Embed Views', value: '2,847', trend: '+12%', color: '#2d8a87' },
        { label: 'Resources Accessed', value: '1,203', trend: '+8%', color: '#2d7a47' },
        { label: 'Quests Completed', value: '89', trend: '+23%', color: '#c9a84c' },
        { label: 'Caretakers Active', value: '147', trend: '+5%', color: '#8a8ab8' },
      ],
    },
  },
];

const C = {
  bg: '#060d12',
  panel: 'rgba(255,255,255,0.02)',
  border: 'rgba(201,168,76,0.12)',
  gold: '#c9a84c',
  goldText: 'rgba(201,168,76,0.6)',
  cream: '#F5F0E8',
  creamFaint: 'rgba(212,197,169,0.5)',
};

// ── Demo Components ───────────────────────────────────────────────────────────

function SubmissionQueueDemo({ items }: { items: typeof STEPS[1]['demo']['items'] }) {
  const [selected, setSelected] = useState<number | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item, i) => (
        <div
          key={i}
          onClick={() => setSelected(selected === i ? null : i)}
          style={{ background: selected === i ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${selected === i ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.1)'}`, borderRadius: 6, padding: '10px 14px', cursor: 'pointer', transition: 'all 0.15s' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: selected === i ? 8 : 0 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 6px', borderRadius: 3, background: item.type === 'resource' ? 'rgba(45,122,71,0.15)' : item.type === 'disparity' ? 'rgba(224,82,82,0.12)' : 'rgba(138,138,184,0.12)', color: item.type === 'resource' ? '#2d7a47' : item.type === 'disparity' ? '#e05252' : '#8a8ab8', textTransform: 'uppercase', letterSpacing: '.06em' }}>{item.type}</span>
            <span style={{ color: C.cream, fontSize: 10, flex: 1 }}>{item.title}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#c9a84c', background: 'rgba(201,168,76,0.1)', padding: '2px 6px', borderRadius: 10 }}>PENDING</span>
          </div>
          {selected === i && (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)' }}>{item.city} · {item.ward} · by {item.submittedBy}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ApprovalControlsDemo({ submission }: { submission: typeof STEPS[2]['demo']['submission'] }) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | 'published'>('pending');
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.12)', padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase' }}>resource</span>
        <span style={{ color: C.cream, fontSize: 10, fontWeight: 600 }}>{submission.title}</span>
      </div>
      <p style={{ color: C.creamFaint, fontSize: 9, lineHeight: 1.6, marginBottom: 12 }}>{submission.content}</p>
      <div style={{ display: 'flex', gap: 6 }}>
        {status === 'pending' ? (
          <>
            <button onClick={() => setStatus('approved')} style={{ flex: 1, padding: '6px', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.35)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', letterSpacing: '.06em' }}>✓ APPROVE</button>
            <button onClick={() => setStatus('published')} style={{ flex: 1, padding: '6px', background: 'rgba(45,138,135,0.12)', border: '1px solid rgba(45,138,135,0.35)', borderRadius: 4, color: '#2d8a87', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', letterSpacing: '.06em' }}>↑ PUBLISH</button>
            <button onClick={() => setStatus('rejected')} style={{ flex: 1, padding: '6px', background: 'rgba(224,82,82,0.08)', border: '1px solid rgba(224,82,82,0.25)', borderRadius: 4, color: '#e05252', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', letterSpacing: '.06em' }}>✕ REJECT</button>
          </>
        ) : (
          <div style={{ flex: 1, padding: '8px', background: status === 'published' ? 'rgba(45,138,135,0.1)' : status === 'approved' ? 'rgba(45,122,71,0.1)' : 'rgba(224,82,82,0.08)', border: `1px solid ${status === 'published' ? 'rgba(45,138,135,0.3)' : status === 'approved' ? 'rgba(45,122,71,0.3)' : 'rgba(224,82,82,0.2)'}`, borderRadius: 4, textAlign: 'center' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: status === 'published' ? '#2d8a87' : status === 'approved' ? '#2d7a47' : '#e05252', letterSpacing: '.08em', textTransform: 'uppercase' }}>
              {status === 'published' ? '↑ Published — Live in Collection' : status === 'approved' ? '✓ Approved — Staged for Publishing' : '✕ Rejected'}
            </span>
            <button onClick={() => setStatus('pending')} style={{ display: 'block', margin: '4px auto 0', fontFamily: 'monospace', fontSize: 7, color: C.creamFaint, background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Reset demo</button>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricsGridDemo({ cities }: { cities: typeof STEPS[3]['demo']['cities'] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
      {cities.map((c, i) => (
        <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '10px 12px' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: C.gold, letterSpacing: '.06em', marginBottom: 8 }}>{c.city}</p>
          {[
            { label: 'Resources', value: c.resources, color: '#2d7a47' },
            { label: 'Disparities', value: c.disparities, color: '#e05252' },
            { label: 'Context', value: c.context, color: '#8a8ab8' },
            { label: 'Pending', value: c.pending, color: '#c9a84c' },
            { label: 'Caretakers', value: c.caretakers, color: '#2d8a87' },
          ].map((m, j) => (
            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{m.label}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 9, color: m.color, fontWeight: 700 }}>{m.value}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ImpactMetricsDemo({ stats }: { stats: typeof STEPS[4]['demo']['stats'] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
      {stats.map((s, i) => (
        <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${s.color}20`, borderRadius: 6, padding: '12px 14px' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 18, color: s.color, fontWeight: 700, marginBottom: 2 }}>{s.value}</p>
          <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>{s.label}</p>
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: s.color, background: `${s.color}12`, padding: '1px 5px', borderRadius: 2 }}>{s.trend} this month</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Onboarding Component ─────────────────────────────────────────────────

export default function WardStewardOnboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const step = STEPS[currentStep];

  const goNext = () => {
    setCompleted(prev => new Set([...prev, currentStep]));
    if (currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  return (
    <section style={{ background: C.bg, borderTop: '1px solid rgba(201,168,76,0.08)', padding: '60px 0' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: C.goldText, marginBottom: 8 }}>
            ◎ WARD STEWARD ONBOARDING · INTERACTIVE WALKTHROUGH
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(14px, 3vw, 22px)', color: C.cream, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Ward Steward Orientation
          </h2>
          <p style={{ color: C.creamFaint, fontSize: 12, lineHeight: 1.7, maxWidth: 560 }}>
            A guided walkthrough of the Ward Steward workflow — submission review, approval controls, metrics interpretation, and community impact measurement.
          </p>
        </div>

        {/* Step progress */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 32 }}>
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => setCurrentStep(i)}
              style={{ flex: 1, padding: '8px 4px', background: i === currentStep ? `${s.color}15` : completed.has(i) ? 'rgba(45,122,71,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${i === currentStep ? s.color + '50' : completed.has(i) ? 'rgba(45,122,71,0.25)' : 'rgba(201,168,76,0.08)'}`, borderRadius: 4, cursor: 'pointer', textAlign: 'center' }}
            >
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: i === currentStep ? s.color : completed.has(i) ? '#2d7a47' : 'rgba(212,197,169,0.25)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 2 }}>
                {completed.has(i) ? '✓' : `0${s.step}`}
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 6, color: i === currentStep ? s.color : 'rgba(212,197,169,0.2)', letterSpacing: '.04em', textTransform: 'uppercase', display: 'none' }} className="step-label">
                {s.title.split(' ')[0]}
              </p>
            </button>
          ))}
        </div>

        {/* Step content */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Left: content */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, border: `1px solid ${step.color}50`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: step.color, background: `${step.color}08` }}>
                {step.icon}
              </div>
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: step.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 2 }}>Step {step.step} of {STEPS.length}</p>
                <h3 style={{ fontFamily: 'monospace', fontSize: 14, color: C.cream, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>{step.title}</h3>
              </div>
            </div>

            <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 11, lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic', borderLeft: `2px solid ${step.color}30`, paddingLeft: 10 }}>
              {step.summary}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {step.content.map((c, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.08)', borderRadius: 6, padding: '10px 14px' }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: step.color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>{c.label}</p>
                  <p style={{ color: C.creamFaint, fontSize: 10, lineHeight: 1.6 }}>{c.text}</p>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              {currentStep > 0 && (
                <button onClick={goPrev} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4, color: C.creamFaint, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', letterSpacing: '.06em' }}>← Back</button>
              )}
              {step.action.next ? (
                <button onClick={goNext} style={{ flex: 1, padding: '10px 16px', background: `${step.color}15`, border: `1px solid ${step.color}40`, borderRadius: 4, color: step.color, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  {step.action.label}
                </button>
              ) : (
                <Link href={step.action.href || '/ward-steward'} style={{ flex: 1, display: 'block', textAlign: 'center', padding: '10px 16px', background: `${step.color}15`, border: `1px solid ${step.color}40`, borderRadius: 4, color: step.color, fontFamily: 'monospace', fontSize: 9, textDecoration: 'none', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  {step.action.label}
                </Link>
              )}
            </div>
          </div>

          {/* Right: demo */}
          <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 8, padding: '16px', overflow: 'hidden' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 12 }}>◈ Interactive Demo</p>
            {step.id === 'welcome' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: '⬡', label: 'Review Submissions', desc: 'Resources, disparities, context from caretakers', color: '#2d7a47' },
                  { icon: '✓', label: 'Approve & Publish', desc: 'Verify accuracy, publish to the collection', color: '#2d8a87' },
                  { icon: '◈', label: 'Read Metrics', desc: 'Collection growth, caretaker activity, reach', color: '#8a8ab8' },
                  { icon: '⌘', label: 'Measure Impact', desc: 'Embed views, resource utilization, quest links', color: '#c9a84c' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: `${item.color}08`, border: `1px solid ${item.color}20`, borderRadius: 5 }}>
                    <span style={{ fontSize: 16, color: item.color }}>{item.icon}</span>
                    <div>
                      <p style={{ fontFamily: 'monospace', fontSize: 9, color: item.color, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 1 }}>{item.label}</p>
                      <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {step.id === 'submission-review' && step.demo && 'items' in step.demo && (
              <SubmissionQueueDemo items={step.demo.items} />
            )}
            {step.id === 'approval-controls' && step.demo && 'submission' in step.demo && (
              <ApprovalControlsDemo submission={step.demo.submission} />
            )}
            {step.id === 'metrics' && step.demo && 'cities' in step.demo && (
              <MetricsGridDemo cities={step.demo.cities} />
            )}
            {step.id === 'impact' && step.demo && 'stats' in step.demo && (
              <ImpactMetricsDemo stats={step.demo.stats} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
